import React, { useEffect, useState } from "react";
import Header from "../_components/common/Header";
import "../_css/TeacherManagement.css";
import { getPlans, updatePlan, getModules, getPlanModules } from "../requests";
import { deleteAuthToken, notifyError, notifySuccess } from "../utils/helper";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../_store/reducers/loaderReducer";
import MultiSelectDropdown from "../_components/common/MultiSelectDropdown";
import _ from "lodash";

const PlanManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);

  // Available modules list
  const [availableModules, setAvailableModules] = useState([]);

  const fetchPlans = async () => {
    dispatch(startLoader());
    try {
      const response = await getPlanModules();
      setPlans(response.data.plans || []);
    } catch (error) {
      if (error?.response?.status === 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(error?.response?.data?.error || "Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };

  const fetchModules = async () => {
    dispatch(startLoader());
    try {
      const response = await getModules();
      setAvailableModules(response.data.modules || []);
    } catch (error) {
      if (error?.response?.status === 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(error?.response?.data?.error || "Something went wrong");
    } finally {
      dispatch(stopLoader());
    }
  };

  useEffect(() => {
    fetchPlans();
    fetchModules();
  }, []);

  // Helper function to format module name (module - sub_module or just module)
  const formatModuleName = (module) => {
    if (!module) return "";
    if (module.sub_module) {
      return `${module.module} - ${module.sub_module}`;
    }
    return module.module;
  };

  // Helper function to get formatted module name by ID
  const getModuleNameById = (moduleId) => {
    const module = availableModules.find((m) => _.toString(m.id) === _.toString(moduleId));
    return module ? formatModuleName(module) : "";
  };

  // Helper function to get module ID by formatted name
  const getModuleIdByName = (formattedName) => {
    const module = availableModules.find((m) => formatModuleName(m) === formattedName);
    return module ? module.id : null;
  };

  const handleEdit = (plan) => {
    const planId = plan.id;
    const planName = plan.name;
    // Convert module IDs to module names for display
    let planModuleIds = plan.md_allow ? plan.md_allow.split(",").filter(Boolean) : [];
    if (planModuleIds.includes("all")) {
      planModuleIds = availableModules.map((module) => module.id);
    }
    const planModuleNames = planModuleIds
      .map((id) => getModuleNameById(_.toString(id).trim()))
      .filter((name) => name !== ""); // Filter out empty names
    
    setEditingId(planId);
    setEditingData({
      id: planId,
      name: planName,
      status: plan.status !== undefined ? plan.status : 1,
      modules: planModuleNames,
      price: plan.price ?? "",
      credits: plan.credits ?? plan.max_credits ?? "",
    });
    setOpen(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData(null);
  };

  const handleSave = async () => {
    if (!editingData.name || editingData.name.trim() === "") {
      notifyError("Plan name is required");
      return;
    }

    dispatch(startLoader());
    try {
      // Convert module names back to IDs and join as comma-separated string
      const moduleIds = editingData.modules
        .map((name) => getModuleIdByName(name))
        .filter((id) => id !== null)
        .map((id) => id.toString());
      
      // Check if all modules are selected, if so send "all"
      const allModuleIds = availableModules.map((m) => m.id.toString());
      const isAllSelected = 
        moduleIds.length === allModuleIds.length &&
        moduleIds.every((id) => allModuleIds.includes(id));
      
      const modulesString = isAllSelected ? "all" : moduleIds.join(",");

      const payload = {
        name: editingData.name,
        status: editingData.status !== undefined ? editingData.status : 1,
        modules: modulesString,
        price: editingData.price ?? "",
        credits: editingData.credits ?? "",
      };

      const payloadWithId = {
        plan_id: editingData.id,
        ...payload,
      };

      const response = await updatePlan(payloadWithId);
      if (response.status < 400) {
        notifySuccess(response?.data?.message || "Plan updated successfully");
        setEditingId(null);
        setEditingData(null);
        fetchPlans();
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(error?.response?.data?.error || "Failed to update plan");
    } finally {
      dispatch(stopLoader());
    }
  };

  const handleToggleStatus = async (plan) => {
    const planId = plan.id;
    const currentStatus = plan.status;
    const newStatus = currentStatus === 1 ? 0 : 1;
    const action = newStatus === 1 ? "activate" : "deactivate";

    if (!window.confirm(`Are you sure you want to ${action} this plan?`)) {
      return;
    }

    dispatch(startLoader());
    try {
      const payload = {
        plan_id: planId,
        name: plan.name,
        status: newStatus,
        modules: plan.md_allow || "",
      };

      const response = await updatePlan(payload);
      if (response.status < 400) {
        notifySuccess(
          response?.data?.message ||
            `Plan ${action}d successfully`
        );
        setOpen(null);
        fetchPlans();
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        notifyError(
          error?.response?.data?.error ||
            "Your session has expired. Please log in again."
        );
        deleteAuthToken();
        navigate("/login");
        return;
      }
      notifyError(
        error?.response?.data?.error || `Failed to ${action} plan`
      );
    } finally {
      dispatch(stopLoader());
    }
  };

  const getModuleName = (moduleId) => {
    const module = availableModules.find((module) => _.toString(module.id) === _.toString(moduleId));
    if(module){
      let subModule = module.sub_module
      if(subModule){
        return module?.module + " - " + subModule;
      }
      return module?.module;
    }
    return "";
  };

  return (
    <div className="page-main" onMouseDown={() => setOpen(null)}>
      <Header name={["Plan Management"]} />
      <div className="teacher-container">
        <div className="teacher-head">
          <div className="teacher-head-text">Plan Management</div>
        </div>
        <div className="teacher-table" style={{ borderTop: "none" }}>
          <div className="teacher-table-head">
            <div className="teacher-table-head-cell" style={{ width: "6%" }}>
              S. No
            </div>
            <div className="teacher-table-head-cell" style={{ width: "18%" }}>
              Plan Name
            </div>
            <div className="teacher-table-head-cell" style={{ width: "26%" }}>
              Allowed Modules
            </div>
            <div className="teacher-table-head-cell" style={{ width: "10%" }}>
              Price
            </div>
            <div className="teacher-table-head-cell" style={{ width: "10%" }}>
              Credits
            </div>
            <div className="teacher-table-head-cell" style={{ width: "10%" }}>
              Status
            </div>
            <div
              className="teacher-table-head-cell"
              style={{ width: "20%", textAlign: "center" }}
            >
              Actions
            </div>
          </div>

          {plans.length === 0 && (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: "#9CA3AF",
              }}
            >
              No plans found
            </div>
          )}

          {plans.length > 0 &&
            plans.map((plan, i) => {
              const planId = plan.id;
              const planName = plan.name;
              let planModules = plan.md_allow ? plan.md_allow.split(",") : [];
              if(planModules.includes("all")) {
                planModules = availableModules.map((module) => module.id);
              }
              const isEditing = editingId === planId;

              return (
                <div
                  key={i}
                  className="teacher-table-row"
                  style={{
                    borderBottom: i === plans.length - 1 && "none",
                  }}
                >
                  <div
                    className="teacher-table-row-cell"
                    style={{ width: "6%" }}
                  >
                    {i + 1}
                  </div>
                  <div
                    className="teacher-table-row-cell"
                    style={{ width: "18%" }}
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingData?.name || ""}
                        onChange={(e) =>
                          setEditingData({
                            ...editingData,
                            name: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                        }}
                      />
                    ) : (
                      planName
                    )}
                  </div>
                  <div
                    className="teacher-table-row-cell"
                    style={{ width: "26%" }}
                  >
                    {isEditing ? (
                      <MultiSelectDropdown
                        options={availableModules.map((m) => formatModuleName(m))}
                        selected={editingData?.modules || []}
                        setSelected={(modules) =>
                          setEditingData({
                            ...editingData,
                            modules,
                          })
                        }
                        placeholder="Select modules..."
                      />
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                        }}
                      >
                        {planModules.length > 0 ? (
                          planModules.map((module, idx) => (
                            <span
                              key={idx}
                              style={{
                                display: "inline-block",
                                padding: "2px 8px",
                                backgroundColor: "#E0F5FE",
                                color: "#01A7DE",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              {getModuleName(module)}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: "#9CA3AF" }}>No modules</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div
                    className="teacher-table-row-cell"
                    style={{ width: "10%" }}
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingData?.price ?? ""}
                        onChange={(e) =>
                          setEditingData({
                            ...editingData,
                            price: e.target.value,
                          })
                        }
                        placeholder="Price"
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                        }}
                      />
                    ) : (
                      plan.price ?? "-"
                    )}
                  </div>
                  <div
                    className="teacher-table-row-cell"
                    style={{ width: "10%" }}
                  >
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingData?.credits ?? ""}
                        onChange={(e) =>
                          setEditingData({
                            ...editingData,
                            credits: e.target.value,
                          })
                        }
                        placeholder="Credits"
                        style={{
                          width: "100%",
                          padding: "6px 8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none",
                        }}
                      />
                    ) : (
                      plan.credits ?? plan.max_credits ?? "-"
                    )}
                  </div>
                  <div
                    className="teacher-table-row-cell"
                    style={{ width: "10%" }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        backgroundColor:
                          plan.status === 1
                            ? "#D1FAE5"
                            : plan.status === 0
                            ? "#FEE2E2"
                            : "#F3F4F6",
                        color:
                          plan.status === 1
                            ? "#065F46"
                            : plan.status === 0
                            ? "#991B1B"
                            : "#6B7280",
                      }}
                    >
                      {plan.status === 1
                        ? "Active"
                        : plan.status === 0
                        ? "Inactive"
                        : "Unknown"}
                    </span>
                  </div>
                  <div
                    className="teacher-table-row-cell"
                    style={{ width: "20%", textAlign: "center" }}
                  >
                    {isEditing ? (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={handleSave}
                          className="my-btn"
                          style={{
                            backgroundColor: "#01A7DE",
                            color: "#FFFFFF",
                            padding: "6px 16px",
                            fontSize: "12px",
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="my-btn"
                          style={{
                            backgroundColor: "#E5E7EB",
                            color: "#030712",
                            padding: "6px 16px",
                            fontSize: "12px",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div
                        className="dropdown"
                        style={{ width: "100%", textAlign: "center" }}
                      >
                        <button
                          onClick={() => setOpen(planId)}
                          className="dropdown-btn"
                        >
                          ⋮
                        </button>

                        {open === planId && (
                          <div className="dropdown-content">
                            <div
                              className="option"
                              onMouseDown={() => handleEdit(plan)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="drp-icon"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M16.862 4.487l1.687 1.687m-2.79-2.79l-9.193 9.193a4.5 4.5 0 00-1.126 2.012l-.376 1.502a.75.75 0 00.94.94l1.502-.376a4.5 4.5 0 002.012-1.126l9.193-9.193m0 0l-1.687-1.687"
                                />
                              </svg>
                              Edit
                            </div>

                            <div
                              className="option"
                              onMouseDown={() => handleToggleStatus(plan)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                className="drp-icon"
                                style={{
                                  color: plan.status === 1 ? "#991B1B" : "#065F46",
                                }}
                              >
                                {plan.status === 1 ? (
                                  // Deactivate icon (X or power off)
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M18.364 5.636a9 9 0 11-12.728 12.728A9 9 0 0118.364 5.636zM8.464 8.464l7.072 7.072"
                                  />
                                ) : (
                                  // Activate icon (checkmark or power on)
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                )}
                              </svg>
                              {plan.status === 1 ? "Deactivate" : "Activate"}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default PlanManagement;
